import { useNavigate, useParams } from "react-router";
import {
  AcceptSharingInvitationDocument,
  SharingInvitationStatus,
  SharingInvitationType,
} from "../../__generated__/graphql";
import { useMutation } from "@apollo/client";
import { useAuth } from "../../hooks/useAuth";
import Logo from "../../components/Logo";
import Button from "../../components/Button";
import { toast } from "react-hot-toast";
import Redirect from "../../components/Redirect";
import FullLoading from "../../components/FullLoading";
import useSharingInvitation from "./hooks/useSharingInvitation";
import { LockClosedIcon, CheckIcon } from "@heroicons/react/24/outline";
import { LuCalendar, LuFile, LuUsers } from "react-icons/lu";
import { MdLibraryAddCheck } from "react-icons/md";
import useCurrentUserOrganizations from "../../hooks/useCurrentUserOrganizations";
import { useState } from "react";
import Modal from "../../components/Modal";
import { cn } from "../../lib/utils";

export default function SharingInvitation() {
  const { isAuthenticated, isAuthLoading, userId } = useAuth();
  const navigateWithLog = useNavigate();

  const { invitationCode } = useParams();

  const { data, loading, error } = useSharingInvitation(invitationCode);
  const {
    data: currentUserOrganizationsData,
    loading: currentUserOrganizationsLoading,
  } = useCurrentUserOrganizations();

  const [acceptSharingInvitation, { loading: acceptSharingInvitationLoading }] =
    useMutation(AcceptSharingInvitationDocument);

  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);

  const fromOrganizationName =
    data?.sharingInvitationByInvitationCode?.fromOrganizationName;
  const invitationType =
    data?.sharingInvitationByInvitationCode?.invitationType;

  if (
    isAuthLoading ||
    acceptSharingInvitationLoading ||
    loading ||
    currentUserOrganizationsLoading
  ) {
    return <FullLoading />;
  }

  if (error) return <div>Error: {error.message}</div>;

  if (
    data?.sharingInvitationByInvitationCode?.status !==
    SharingInvitationStatus.Pending
  ) {
    localStorage.removeItem("invitationCode");
    return <Redirect to="/" />;
  }

  const handleAcceptSharingInvitation = async () => {
    if (invitationType === SharingInvitationType.OrganizationMember) {
      const fromOrganizationId =
        data?.sharingInvitationByInvitationCode?.fromOrganizationId;
      localStorage.setItem("invitedToOrganizationId", fromOrganizationId || "");
    }

    if (!isAuthenticated) {
      localStorage.setItem("invitationCode", invitationCode || "");
      navigateWithLog("/signup");
      return;
    }

    const organizations =
      currentUserOrganizationsData?.organizations?.filter((org) =>
        org?.users?.some((user) => user?.userId === userId)
      ) || [];

    // If user has multiple organizations, show modal to select one
    if (organizations.length > 1) {
      setIsOrganizationModalOpen(true);
      return;
    }

    // If user has only one organization, use it directly
    const organizationId =
      organizations.length === 1
        ? organizations[0]?.id
        : localStorage.getItem("organizationId");

    if (!organizationId) {
      localStorage.setItem("invitationCode", invitationCode || "");
      navigateWithLog("/create-organization");
      return;
    }

    if (
      data.sharingInvitationByInvitationCode?.invitationType ===
      SharingInvitationType.OrganizationMember
    ) {
      navigateWithLog(`/join-organization?invitationCode=${invitationCode}`);
      return;
    }

    await acceptWithOrganization(organizationId);
  };

  const acceptWithOrganization = async (organizationId: string) => {
    try {
      const { data: acceptSharingInvitationData } =
        await acceptSharingInvitation({
          variables: {
            acceptSharingInvitationInput: {
              invitationCode: invitationCode || "",
              toOrganizationId: organizationId,
            },
          },
        });

      if (acceptSharingInvitationData?.acceptSharingInvitation) {
        localStorage.removeItem("invitationCode");
        toast.success("Invitation acceptée avec succès");
        navigateWithLog("/");
      } else {
        toast.error(
          "Une erreur est survenue lors de l'acceptation de l'invitation"
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de l'acceptation de l'invitation"
      );
    }
  };

  const handleSelectOrganization = () => {
    if (!selectedOrganizationId) {
      toast.error("Veuillez sélectionner une organisation");
      return;
    }

    if (
      data.sharingInvitationByInvitationCode?.invitationType ===
      SharingInvitationType.OrganizationMember
    ) {
      setIsOrganizationModalOpen(false);
      navigateWithLog(`/join-organization?invitationCode=${invitationCode}`);
      return;
    }

    setIsOrganizationModalOpen(false);
    acceptWithOrganization(selectedOrganizationId);
  };

  if (
    userId &&
    data?.sharingInvitationByInvitationCode?.fromPublicUserOrganization.id ===
      userId
  ) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-background px-4 text-center">
        <div className="text-3xl md:text-4xl font-extrabold text-accent">
          Oups.
        </div>
        <p className="text-secondary text-sm md:text-base">
          Vous ne pouvez pas accepter votre propre invitation.
        </p>
        <Button
          variant="secondary"
          className="mt-8"
          onClick={() => {
            localStorage.removeItem("invitationCode");
            navigateWithLog("/");
          }}
        >
          Revenir sur mon compte
        </Button>
      </div>
    );
  }

  const invitationWording = () => {
    switch (invitationType) {
      case SharingInvitationType.OrganizationMember:
        return `Acceptez cette invitation et rejoignez l'organisation ${fromOrganizationName}.`;
      case SharingInvitationType.ExternalMember:
        return `${fromOrganizationName} vous invite à collaborer et à partager des informations essentielles pour vos projets.`;
    }
  };

  const kontrolPresentation = () => {
    return (
      <div className="flex flex-col gap-3 w-full md:w-1/2 items-center pt-4 md:pt-0">
        <div className="text-2xl md:text-3xl font-bold">Kontrol</div>
        <div className="flex flex-col gap-1 text-sm items-center">
          <LockClosedIcon className="w-6 h-6 md:w-7 md:h-7 text-accent" />
          <div className="text-secondary text-center text-xs md:text-sm">
            Votre propre espace sécurisé pour gérer vos collaborations et vos
            projets artistiques
          </div>
        </div>

        <div className="flex flex-col gap-1 text-sm items-center">
          <LuUsers className="w-6 h-6 md:w-7 md:h-7 text-accent" />
          <div className="text-secondary text-center text-xs md:text-sm">
            Partagez vos documents, vos évènements et vos projets pour faciliter
            la communication
          </div>
        </div>

        <div className="flex flex-col gap-1 text-sm items-center">
          <LuFile className="w-6 h-6 md:w-7 md:h-7 text-accent" />
          <div className="text-secondary text-center text-xs md:text-sm">
            Un outil flexible qui centralise toutes les informations dont vous
            avez besoin
          </div>
        </div>

        <div className="flex flex-col gap-1 text-sm items-center">
          <LuCalendar className="w-6 h-6 md:w-7 md:h-7 text-accent" />
          <div className="text-secondary text-center text-xs md:text-sm">
            Une gestion simplifiée du planning de vos collaborateurs
          </div>
        </div>

        {/* <div className="flex flex-col gap-1 text-sm items-center">
          <LuTicket className="w-7 h-7 text-accent" />
          <div className="text-secondary text-center">
            Accédez et donnez accès à la billetterie de vos spectacles
          </div>
        </div> */}
      </div>
    );
  };

  const organizations =
    currentUserOrganizationsData?.organizations?.filter((org) =>
      org?.users?.some((user) => user?.userId === userId)
    ) || [];

  return (
    <div className="flex flex-col justify-center items-center min-h-screen bg-background text-primary px-4 py-8">
      <Logo />
      <div className="bg-dark border border-border p-4 md:p-6 rounded-md w-full max-w-md md:max-w-3xl mx-auto mt-4 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 md:gap-2">
          <div className="w-full md:w-1/2 flex flex-col justify-between bg-card rounded-md p-4 border border-border">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 md:gap-4">
                <MdLibraryAddCheck className="w-5 h-5 md:w-6 md:h-6 text-accent flex-shrink-0" />
                <h1 className="text-xl md:text-2xl font-bold">
                  {fromOrganizationName}
                  <br />
                  vous invite !
                </h1>
              </div>
              <p className="text-secondary text-xs md:text-sm">
                {invitationWording()}
              </p>
            </div>

            <Button
              className="mt-4"
              onClick={handleAcceptSharingInvitation}
              isLoading={acceptSharingInvitationLoading}
              dataTestId="accept-invitation-button"
            >
              Accepter l'invitation
            </Button>
          </div>

          {kontrolPresentation()}
        </div>
      </div>

      <Modal
        isModalOpen={isOrganizationModalOpen}
        onClose={() => {
          setIsOrganizationModalOpen(false);
          setSelectedOrganizationId(null);
        }}
        title="Sélectionnez une organisation"
        description="Choisissez l'organisation pour laquelle vous souhaitez accepter cette invitation."
        className="max-w-md"
      >
        <div className="flex flex-col gap-4 mt-4">
          {organizations.map((organization) => (
            <div
              key={organization?.id}
              className={cn(
                "flex flex-col gap-2 border border-border rounded-md p-4 w-full bg-dark cursor-pointer hover:bg-dark/80 transition-all duration-300 hover:border-accent",
                selectedOrganizationId === organization?.id && "border-accent"
              )}
              onClick={() =>
                setSelectedOrganizationId(organization?.id || null)
              }
            >
              <div className="flex items-center gap-2">
                {selectedOrganizationId === organization?.id && (
                  <div className="bg-accent rounded-full p-1">
                    <CheckIcon className="w-4 h-4" />
                  </div>
                )}
                <div className="flex flex-col">
                  <div className="text-primary text-xs">
                    Nom de l'organisation
                  </div>
                  <div className="text-accent">{organization?.name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-6">
          <Button
            variant="secondary"
            onClick={() => {
              setIsOrganizationModalOpen(false);
              setSelectedOrganizationId(null);
            }}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSelectOrganization}
            isLoading={acceptSharingInvitationLoading}
            disabled={!selectedOrganizationId}
            className="flex-1"
          >
            Accepter
          </Button>
        </div>
      </Modal>
    </div>
  );
}
